import { LineChart } from '@/components/charts/LineChart';
import { Loader } from '@/components/Loader';
import { useWidgetData } from '@/sections/insights/metrics/hooks';
import { formatDate } from '@/utils/date.util';

function BaseComponent({
  data,
  dot,
}: {
  data: {
    name: string;
    jobs: number;
  }[];
  dot?: boolean;
}) {
  return (
    <LineChart
      dot={dot}
      data={data}
      lines={[
        {
          dataKey: 'jobs',
          dotClassName: 'stroke-chariot-brand-primary',
          lineClassName:
            '[&>path]:stroke-chariot-brand-primary text-chariot-brand-primary',
        },
      ]}
    />
  );
}

export function JobsRunningOverTime() {
  const { data, isLoading } = useWidgetData(
    'statistic',
    () => {
      // Query for job running statistics from 24 hours ago to now
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const dateStr = twentyFourHoursAgo.toISOString().split('T')[0];
      return `#job#running#${dateStr}#`;
    },
    jobStats => {
      // Guard against undefined data during loading
      if (!jobStats || !Array.isArray(jobStats)) {
        return [];
      }

      // Group by 5-minute intervals and aggregate average running jobs across all capabilities
      // The backend provides fractional values where each job's proportion of
      // time running in a minute (0.0-1.0) is recorded and summed across all jobs
      const jobsByInterval = jobStats.reduce(
        (
          acc: Record<number, { sum: number; count: number }>,
          stat: { created?: string; data?: { values?: Record<string, number> } }
        ) => {
          if (!stat?.created || !stat?.data?.values) {
            return acc;
          }

          // Parse the timestamp and truncate to 5-minute intervals
          const timestamp = new Date(stat.created);
          const minutes = timestamp.getMinutes();
          const roundedMinutes = Math.floor(minutes / 5) * 5;
          const intervalKey = new Date(
            timestamp.getFullYear(),
            timestamp.getMonth(),
            timestamp.getDate(),
            timestamp.getHours(),
            roundedMinutes
          ).getTime();

          // Sum all capability fractional values for this minute
          // Each value represents the average job occupancy for that minute
          const avgJobsThisMinute = Object.values(
            stat.data.values as Record<string, number>
          ).reduce((sum, value) => sum + value, 0);

          // Accumulate sum and count for calculating 5-minute interval average
          if (!acc[intervalKey]) {
            acc[intervalKey] = { sum: 0, count: 0 };
          }
          acc[intervalKey].sum += avgJobsThisMinute;
          acc[intervalKey].count += 1;

          return acc;
        },
        {} as Record<number, { sum: number; count: number }>
      );

      // Convert to array and sort by interval
      const sortedData = Object.entries(jobsByInterval)
        .map(([intervalTimestamp, stats]) => {
          const { sum, count } = stats as { sum: number; count: number };
          return {
            timestamp: parseInt(intervalTimestamp),
            avgJobs: sum / count,
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      // Format for the chart
      return sortedData.map(({ timestamp, avgJobs }) => ({
        name: formatDate(
          new Date(timestamp).toISOString(),
          'MMM d, h:mma',
          false
        ), // Disable relative formatting
        jobs: Math.round(avgJobs * 10) / 10, // Round to nearest tenth
      }));
    },
    {
      to: `#statistic#job#running#now&`,
    }
  );

  if (isLoading) {
    return <Loader type="spinner" isLoading={true} className="h-full" />;
  }

  return <BaseComponent data={data} />;
}
